import React, { useState, useRef, useEffect } from 'react';
import { IconSearch } from '../../../icons';

interface AutocompleteSelectProps {
  titulo: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  searchPlaceholder?: string;
}

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  titulo,
  placeholder,
  value,
  onChange,
  options,
  searchPlaceholder = "Buscar..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const filtered = options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    // Só abre o dropdown se não for clique na área da seta
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const inputWidth = rect.width;
    
    // Se o clique foi na área da direita (onde está a seta), não abre o dropdown
    if (clickX > inputWidth - 48) {
      return;
    }
    
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--c2d3748)',
        marginBottom: '0.5rem',
        display: 'block'
      }}>
        {titulo}
      </label>
      
      <div ref={containerRef} style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onClick={handleInputClick}
          style={{
            width: '100%',
            height: '48px',
            padding: '0 1rem',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: 'var(--surface-input)',
            color: 'var(--c2d3748)'
          }}
        />
        
        {/* Área clicável da seta do dropdown */}
        <div 
          onClick={() => setIsOpen(!isOpen)}
          style={{
            position: 'absolute',
            right: '0px',
            top: '0px',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1
          }}
        >
          {/* Seta do dropdown */}
          <div style={{
            color: 'var(--c718096)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            pointerEvents: 'none'
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M6 8L2 4h8L6 8z" />
            </svg>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--ce2e8f0)',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflow: 'hidden'
          }}>
            {/* Campo de busca */}
            <div style={{
              padding: '12px',
              borderBottom: '1px solid #e2e8f0',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--c718096)',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconSearch color="#718096" />
              </div>
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  height: '36px',
                  padding: '0 0 0 40px',
                  border: '1px solid var(--ce2e8f0)',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  outline: 'none',
                  backgroundColor: 'var(--surface-input)',
                  color: 'var(--c2d3748)'
                }}
                autoFocus
              />
            </div>

            {/* Lista de opções */}
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelect(option)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: 'var(--c2d3748)',
                      borderBottom: index < filteredOptions.length - 1 ? '1px solid var(--cf7fafc)' : 'none',
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--ce2e8f0)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {option}
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '12px 16px',
                  fontSize: '0.875rem',
                  color: 'var(--c718096)',
                  textAlign: 'center'
                }}>
                  Nenhum resultado encontrado
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutocompleteSelect;
